import React, { useState } from "react";
import { object, func } from "prop-types";
import { connect } from "react-redux";
import { WithMember } from "@quintype/components";
import get from "lodash/get";
import ImageUploading from "react-images-uploading";

import Button from "../../atoms/Button";
import TextField from "../../atoms/TextField";
import DatePicker from "../../atoms/DatePicker";
import GenderIcon from "../../atoms/icons/gender";
import ProfileHeadline from "../../atoms/ProfileHeadline";
import MessageView from "../../atoms/MessageView";
import ContributorImage from "../../atoms/ContributorImage";
import moment from "moment";
import { staticTexts } from "../../utils/Translate";
import Plus from "../../atoms/icons/Plus";

import { updateUserInformation, signImage, memberProfileUpdate, uploadS3ToTemp } from "../../helper/api";
import { setLoading } from "../../helper/actions";

import "./edit-profile.m.css";

const EditProfile = ({ member, setLoading, navigateToPage, publisherAttributes }) => {
  const [error, setError] = useState(null);
  const [name, setName] = useState(member.name || "");
  const [nameError, setNameError] = useState(null);
  const [metadata, setMetadata] = useState(member.metadata || {});
  const [selectedGender, setSelectedGender] = useState(metadata.gender ? metadata.gender : "male"); // default male
  const [birthday, setBirthday] = useState(metadata.dob ? moment(metadata.dob) : null);
  const [tempImageObj, setTempImageObj] = useState(null);
  const [blurImage, setBlurImage] = useState(false);

  const genderSelection = type => {
    setSelectedGender(type);
  };

  const setData = e => {
    const metadataObj = { ...metadata };
    const fieldName = e.target.name;
    metadataObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && metadataObj[`${fieldName}Error`]) {
      metadataObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setMetadata(metadataObj);
  };

  const nameChange = e => {
    // If error has been set then remove it
    if (e.target.value && nameError) {
      setNameError(null);
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setName(e.target.value);
  };

  const dateSelected = newDate => setBirthday(newDate);

  const onSubmitClick = (e, checkForMemberUpdated) => {
    if (name.length === 0) {
      return;
    }
    e.preventDefault();

    const data = { ...metadata };
    data.gender = selectedGender;

    if (birthday) {
      const date = new Date(birthday);
      let dd = date.getDate();
      const mm = date.getMonth() + 1;
      const yyyy = date.getFullYear();

      if (dd < 10) {
        dd = `0${dd}`;
      }

      data.dob = `${dd}-${mm}-${yyyy}`;
    }

    setLoading(true);

    updateUserInformation({ metadata: data })
      .then(() => {
        checkForMemberUpdated();
        if (member.name !== name) {
          memberProfileUpdate({ name: name }).then(resp => resp.name !== member.name && navigateToPage("/profile"));
        } else {
          navigateToPage("/profile");
        }
      })
      .catch(ex => setError(ex.message || "Please try after some time."))
      .finally(() => setLoading(false));
  };

  const prepareFormData = (keys, formdata, imageList, res) => {
    return keys.map(key => {
      if (key === "file") {
        return formdata.append(key, imageList[0].file);
      } else {
        return formdata.append(key, res[key]);
      }
    });
  };

  // Profile pic change
  const onProfileChange = imageList => {
    setBlurImage(true);
    signImage(imageList[0].file.name, imageList[0].file.type).then(res => {
      const formdata = new FormData();
      const keys = [
        "key",
        "Content-Type",
        "policy",
        "acl",
        "success_action_status",
        "AWSAccessKeyId",
        "signature",
        "file"
      ];

      prepareFormData(keys, formdata, imageList, res);

      uploadS3ToTemp(res, formdata).then(response => {
        if (response.status === 201) {
          memberProfileUpdate({ s3Key: res.key })
            .then(resp => {
              if (!resp) {
                setBlurImage(false);
                return;
              }
              setTempImageObj(resp);
              setBlurImage(false);
            })
            .catch(err => {
              setBlurImage(false);
              console.log(err);
            });
        }
      });
    });
  };

  return (
    <WithMember>
      {({ checkForMemberUpdated }) => (
        <form onSubmit={e => onSubmitClick(e, checkForMemberUpdated)}>
          <div styleName="container">
            <ProfileHeadline
              title={staticTexts(publisherAttributes.lang).editProfile}
              arrowClick={() => navigateToPage("/profile")}
            />
            <ImageUploading onChange={onProfileChange}>
              {({ imageList, onImageUpload, onImageRemoveAll }) => (
                <React.Fragment>
                  <div styleName="profile-pic">
                    <div styleName="with-upload-wrapper">
                      <ContributorImage authorObj={tempImageObj || member} customStyle={true} blurStyle={blurImage} />
                      <button styleName="upload-image" type="button" onClick={onImageUpload}>
                        <Plus />
                      </button>
                    </div>
                    <span styleName="email-label">{staticTexts(publisherAttributes.lang).emailAddress}</span>
                    <span styleName="email-value">{member.email}</span>
                  </div>
                </React.Fragment>
              )}
            </ImageUploading>

            <div styleName="details">
              <span styleName="personal-info">{staticTexts(publisherAttributes.lang).personalInformation}</span>

              <div styleName="gender-wrapper">
                <span styleName="gender-label">{staticTexts(publisherAttributes.lang).gender}</span>
                <div styleName="gender">
                  <div
                    styleName={`male-view ${selectedGender === "male" ? "selected-gender" : "unselected-gender"}`}
                    onClick={() => genderSelection("male")}
                  >
                    <GenderIcon type="male" />
                  </div>
                  <div
                    styleName={`female-view ${selectedGender === "female" ? "selected-gender" : "unselected-gender"}`}
                    onClick={() => genderSelection("female")}
                  >
                    <GenderIcon type="female" />
                  </div>
                </div>
              </div>

              <div styleName="column-fields">
                <TextField
                  value={name}
                  error={nameError}
                  label={staticTexts(publisherAttributes.lang).name}
                  name="name"
                  inputType="type2"
                  onChange={nameChange}
                  required
                />

                <TextField
                  value={metadata.address}
                  error={metadata.addressError}
                  label={staticTexts(publisherAttributes.lang).countryOrRegion}
                  name="address"
                  inputType="type2"
                  onChange={setData}
                />
              </div>

              <div styleName="column-fields">
                <TextField
                  value={metadata["contact-number"]}
                  error={metadata["contact-numberError"]}
                  label={staticTexts(publisherAttributes.lang).contactNumber}
                  name="contact-number"
                  inputType="type2"
                  onChange={setData}
                  type="number"
                  styleName="contact-number"
                />

                <div styleName="birthday-view">
                  <label styleName="datepicker-label">{staticTexts(publisherAttributes.lang).birthday}</label>
                  <DatePicker dateSelected={dateSelected} date={birthday} placeholder="" />
                </div>
              </div>

              <MessageView type="error" text={error} />

              <div styleName="column-fields">
                <Button content={staticTexts(publisherAttributes.lang).save} buttonType="type4" type="submit" />
                <div />
              </div>
            </div>
          </div>
        </form>
      )}
    </WithMember>
  );
};

EditProfile.propTypes = {
  member: object.isRequired,
  navigateToPage: func,
  setLoading: func,
  publisherAttributes: object
};

const mapStateToProps = state => ({
  member: state.member || null,
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {})
});

const mapDispatchToProps = dispatch => ({
  navigateToPage: url => global.app.navigateToPage(dispatch, url),
  setLoading: isLoading => dispatch(setLoading(isLoading))
});

export default connect(mapStateToProps, mapDispatchToProps)(EditProfile);
